/**
 * Idenza design system tokens.
 *
 * Source of truth: docs/DESIGN_SYSTEM.md
 *
 * Import by named property to keep tree-shaking honest:
 *   import { color, space, text, radius, shadow, motion, chart } from '@/design/tokens'
 *
 * NEVER hard-code hex, px, or ms in a component. If you need a value not in
 * this file, add it here first with a semantic name. That is the whole point.
 */

// ─────────────────────────────────────────────────────────────────────────
// COLOR — warm-slate neutrals + muted purple brand + WCAG-tuned semantics
// ─────────────────────────────────────────────────────────────────────────
export const color = {
  // ─── Theme-varying surfaces & text ───────────────────────────────────
  // Resolved from CSS variables in globals.css. Flip via
  // <html data-theme="dark">. Every inline style using these tokens
  // automatically switches with the theme.
  bg: {
    app:     "var(--bg-app)",
    surface: "var(--bg-surface)",
    subtle:  "var(--bg-subtle)",
    hover:   "var(--bg-hover)",
    inverse: "var(--bg-inverse)",
  },

  border: {
    subtle:   "var(--border-subtle)",
    strong:   "var(--border-strong)",
    hairline: "var(--border-hairline)",
    inverse:  "var(--border-strong)",
  },

  text: {
    primary:   "var(--text-primary)",
    secondary: "var(--text-secondary)",
    muted:     "var(--text-muted)",
    tertiary:  "var(--text-tertiary)",
    inverse:   "var(--text-inverse)",
    link:      "var(--text-link)",
  },

  // ─── Brand — Idenza cobalt (matches the logo). Same value both themes.
  brand: {
    solid:    "#0B57A4",
    hover:    "#094882",
    active:   "#063865",
    subtle:   "var(--brand-subtle)",   // slight opacity tweak in dark theme
    border:   "var(--brand-border)",
    text:     "var(--text-link)",       // brand cobalt light / lighter cobalt dark
    contrast: "#FFFFFF",
  },

  // Semantic — used sparingly: text tone + 8% tint background.
  // Never as card borders or persistent accents. Data-driven only.
  success: {
    solid: "#0F7A3D",
    bg: "rgba(15,122,61,0.08)",
    border: "rgba(15,122,61,0.16)",
    text: "#0F7A3D",
  },
  warning: {
    solid: "#B54708",
    bg: "rgba(181,71,8,0.08)",
    border: "rgba(181,71,8,0.16)",
    text: "#B54708",
  },
  danger: {
    solid: "#B42318",
    bg: "rgba(180,35,24,0.08)",
    border: "rgba(180,35,24,0.16)",
    text: "#B42318",
  },
  info: {
    // Kept for compat; discourage new usage — prefer neutral in body.
    solid: "#175CD3",
    bg: "rgba(23,92,211,0.08)",
    border: "rgba(23,92,211,0.16)",
    text: "#175CD3",
  },

  overlay: {
    scrim: "rgba(10,10,10,0.40)",
    scrimSubtle: "rgba(10,10,10,0.12)",
  },

  // Role palette — all cool-family (blue/cyan/indigo) to stay on-brand
  // with the Idenza cobalt. Only red breaks the family for alerts, which
  // is non-negotiable (users read red as danger everywhere).
  //
  // Applied as: thin accent bars (2-3px top/left), header icons,
  // inline pills. NEVER as full backgrounds.
  role: {
    live:       { solid: "#0891B2", bg: "var(--role-live-bg)",       text: "#0E7490" }, // cyan-teal — real-time
    ai:         { solid: "#4F46E5", bg: "var(--role-ai-bg)",         text: "#3730A3" }, // indigo — AI/agent
    alert:      { solid: "#DC2626", bg: "var(--role-alert-bg)",      text: "#B91C1C" }, // red — incidents
    compliance: { solid: "#0B57A4", bg: "var(--role-compliance-bg)", text: "#0B57A4" }, // brand cobalt
    neutral:    { solid: "#0A0A0A", bg: "var(--bg-subtle)",          text: "var(--text-primary)" }, // charcoal — neutral KPIs
  },

  // Dark trophy panel — deep NAVY (matches brand family), not pure black.
  // One per screen (Stripe "Volume today" move). Chargeback deflection
  // $-saved lives here.
  dark: {
    bg:      "#0A1E38",              // deep navy, cousin to brand cobalt
    surface: "#122843",              // slightly lighter for internal panels
    border:  "rgba(255,255,255,0.10)",
    text:    "#FFFFFF",
    muted:   "rgba(255,255,255,0.70)",
    faint:   "rgba(255,255,255,0.45)",
    accent:  "#4F8BE0",              // bright cobalt for accents on dark
  },
};

// ─────────────────────────────────────────────────────────────────────────
// SPACING — 4px base, semantic names
// ─────────────────────────────────────────────────────────────────────────
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,   // default card padding, tight surface
  6: 24,   // default card padding, comfortable
  8: 32,   // section gap
  10: 40,  // large section gap
  12: 48,
  16: 64,
  20: 80,
};

// ─────────────────────────────────────────────────────────────────────────
// BORDER RADIUS
// ─────────────────────────────────────────────────────────────────────────
export const radius = {
  none: 0,
  sm: 4,      // inputs, tight buttons, status pills
  md: 6,      // buttons, cards (Stripe/Linear standard)
  lg: 8,      // outer container cards (Attio standard)
  xl: 10,     // modals
  xxl: 12,    // rarely used
  pill: 9999, // chips (pill variant), circular icons
};

// ─────────────────────────────────────────────────────────────────────────
// TYPOGRAPHY
// ─────────────────────────────────────────────────────────────────────────
export const font = {
  sans: `'Inter Variable', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif`,
  mono: `'Geist Mono', 'JetBrains Mono', 'Menlo', ui-monospace, monospace`,
};

export const text = {
  // Sizes (px)
  xs: 11,      // meta, timestamps
  sm: 12,      // uppercase labels, help
  base: 13,    // table cells, dense UI
  md: 14,      // body
  lg: 16,      // emphasized body, card intro
  xl: 20,      // card title
  "2xl": 24,   // section title
  "3xl": 32,   // KPI number
  "4xl": 48,   // hero KPI
};

export const weight = {
  regular: 400,
  medium: 500,   // emphasis
  semibold: 600, // headings, KPI numbers (never use 700)
};

export const leading = {
  tight: 1,    // KPI numbers
  snug: 1.2,   // headings
  normal: 1.4, // body
  relaxed: 1.6,
};

export const tracking = {
  tight: "-0.02em",
  normal: "0",
  wide: "0.04em",
  wider: "0.06em",   // uppercase labels (subtle authority signal)
  widest: "0.08em",
};

// Numeric-specific — always tabular for money, IDs, timestamps.
export const numeric = {
  tabular: {
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
  },
};

// ─────────────────────────────────────────────────────────────────────────
// SHADOWS — very restrained, only for floating elements
// ─────────────────────────────────────────────────────────────────────────
// Shadows are reserved for FLOATING elements only (popovers, dropdowns,
// toasts). Cards, buttons, and inline pills use borders. Ambient shadows
// on internal cards are a "startup" tell — no modern enterprise dashboard
// uses them.
export const shadow = {
  none: "none",
  sm: "0 1px 2px rgba(10, 10, 10, 0.05)",           // dropdown/tooltip
  md: "0 4px 12px -2px rgba(10, 10, 10, 0.08)",     // popover
  lg: "0 12px 24px -6px rgba(10, 10, 10, 0.12)",    // modal
  xl: "0 24px 48px -12px rgba(10, 10, 10, 0.20)",   // command palette

  // Focus rings — brand cobalt at low opacity, always visible on :focus-visible.
  focus: `0 0 0 3px rgba(11, 87, 164, 0.20)`,
  focusDanger: `0 0 0 3px rgba(180, 35, 24, 0.20)`,
};

// ─────────────────────────────────────────────────────────────────────────
// MOTION
// ─────────────────────────────────────────────────────────────────────────
export const duration = {
  fast: 100,       // hover
  base: 150,       // small state changes
  med: 200,        // panel transitions
  slow: 300,       // page-level changes
  pageswitch: 500, // route change (rarely exceed)
};

export const ease = {
  standard: "cubic-bezier(0.4, 0, 0.2, 1)",
  entrance: "cubic-bezier(0, 0, 0.2, 1)",
  exit: "cubic-bezier(0.4, 0, 1, 1)",
  linear: "linear",
  // Spring for personality moments (only where warranted).
  // For Framer Motion: { type: "spring", stiffness: 400, damping: 30 }
  spring: { stiffness: 400, damping: 30 },
};

// Shorthand transition builder: motion.tx('all', 'base')
export const motion = {
  duration,
  ease,
  tx: (property = "all", d = "base", e = "standard") =>
    `${property} ${duration[d]}ms ${ease[e]}`,
};

// ─────────────────────────────────────────────────────────────────────────
// Z-INDEX
// ─────────────────────────────────────────────────────────────────────────
export const zIndex = {
  base: 0,
  raised: 10,
  dropdown: 1000,
  sticky: 1020,
  overlay: 1040,
  modal: 1050,
  popover: 1060,
  toast: 1080,
  tooltip: 1090,
};

// ─────────────────────────────────────────────────────────────────────────
// CHART PALETTE — 6 categorical + rules
// ─────────────────────────────────────────────────────────────────────────
export const chart = {
  // Categorical order — use in this exact order for consistency across charts.
  categorical: [
    "#7F56D9", // brand purple
    "#12B76A", // green
    "#175CD3", // blue
    "#F79009", // amber
    "#EE46BC", // pink
    "#06AED4", // teal
  ],

  // Sequential ramps for heatmaps / density
  sequential: {
    brand: ["#F4EBFF", "#E9D7FE", "#D6BBFB", "#B692F6", "#9E77ED", "#7F56D9"],
    danger: ["#FEF3F2", "#FEE4E2", "#FECDCA", "#FDA29B", "#F97066", "#F04438"],
  },

  // Diverging (for delta / signed values)
  diverging: {
    fromDanger: "#F97066",
    center: "#E7E5E4",
    toSuccess: "#32D583",
  },

  // Grid / axis
  gridStroke: color.border.subtle,
  gridOpacity: 0.4,
  axisText: color.text.tertiary,
  axisTextSize: 11,

  // Line/area
  lineStrokeWidth: 2,
  areaFillOpacity: 0.08,
  barRadius: 4,
  barCategoryWidthRatio: 0.6,

  // Reference lines
  thresholdStroke: color.warning.solid,
  interventionStroke: color.brand.solid,
  refDash: "6 4",
};

// ─────────────────────────────────────────────────────────────────────────
// LAYOUT
// ─────────────────────────────────────────────────────────────────────────
export const layout = {
  contentMaxWidth: 1440,       // data-heavy views
  contentMaxWidthNarrow: 1280, // marketing / settings pages
  gridColumns: 12,
  gridGutter: space[6],        // 24
};

// ─────────────────────────────────────────────────────────────────────────
// Convenience: single default export for cases where a component wants
// the whole thing as one object.
// ─────────────────────────────────────────────────────────────────────────
const tokens = {
  color, space, radius, font, text, weight, leading, tracking, numeric,
  shadow, duration, ease, motion, zIndex, chart, layout,
};

export default tokens;
